import React, { useMemo } from "react";
import { EMPLOYEE_STATUS_OPTIONS, UI } from "./employeeStyles";
import { statusLabelFromRaw } from "../../../../utils/userStatus";

function normalizeStatus(raw) {
  return statusLabelFromRaw(raw);
}

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase();
}

function formatPhone(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return phone || null;
}

export default function EmployeeDirectory({
  employees,
  loading,
  onRefresh,
  onOpen,
  onUpdateStatus,
  directoryState,
  setDirectoryState,
}) {
  const searchTerm  = directoryState.searchTerm  ?? "";
  const filterRole  = directoryState.filterRole  ?? "tutor";
  const filterStatus = directoryState.filterStatus ?? "All";
  const setSearch = (v) => setDirectoryState((p) => ({ ...p, searchTerm: v }));
  const setRole   = (v) => setDirectoryState((p) => ({ ...p, filterRole: v }));
  const setFilter = (v) => setDirectoryState((p) => ({ ...p, filterStatus: v }));
  const filtered = useMemo(() => {
    const term       = searchTerm.trim().toLowerCase();
    const filterNorm = String(filterStatus).trim().toLowerCase();
    const roleNorm   = String(filterRole).trim().toLowerCase();

    return (employees || [])
      .filter((e) => {
        const eRole = (e.role || "tutor").toLowerCase();
        if (roleNorm !== "all" && eRole !== roleNorm) return false;
        const name       = String(e.name || "").toLowerCase();
        const statusNorm = normalizeStatus(e.status).toLowerCase();
        if (term && !name.includes(term)) return false;
        if (filterNorm !== "all" && statusNorm !== filterNorm) return false;
        return true;
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [employees, searchTerm, filterStatus, filterRole]);

  return (
    <div style={UI.page}>
      {/* Page Header */}
      <div style={UI.pageHeader}>
        <div>
          <div style={UI.pageTitle}>Employee Directory</div>
          {!loading && (
            <div style={UI.pageSubtitle}>
              {filtered.length} {filtered.length === 1 ? "employee" : "employees"}
            </div>
          )}
        </div>
        <button style={UI.iconBtn} onClick={onRefresh}>
          <span style={{ fontSize: 14 }}>↻</span> Refresh
        </button>
      </div>

      {/* Controls */}
      <div style={UI.controlsBar}>
        <div style={UI.searchWrap}>
          <input
            style={UI.searchInput}
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(e) => setSearch(e.target.value)}
          />
          {searchTerm && (
            <button style={UI.searchClear} onClick={() => setSearch("")} title="Clear">
              ×
            </button>
          )}
        </div>

        <select style={UI.select} value={filterRole} onChange={(e) => setRole(e.target.value)}>
          <option value="tutor">Tutors</option>
          <option value="admin">Admins</option>
          <option value="all">All Roles</option>
        </select>

        <select style={UI.select} value={filterStatus} onChange={(e) => setFilter(e.target.value)}>
          <option value="All">All Status</option>
          {EMPLOYEE_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Employee List */}
      <div style={UI.listCard}>
        <div style={UI.listScroll}>
          {loading ? (
            <div style={UI.emptyState}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={UI.emptyState}>No employees found.</div>
          ) : (
            filtered.map((e, idx) => {
              const status    = normalizeStatus(e.status);
              const statusKey = status.toLowerCase();
              const initials  = getInitials(e.name);
              const role      = (e.role || "tutor").toLowerCase();
              const phone     = formatPhone(e.phone);
              const isLast    = idx === filtered.length - 1;

              return (
                <div
                  key={e.id}
                  style={{
                    ...UI.empRow,
                    borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                  }}
                  onClick={() => onOpen(e.id)}
                  onMouseEnter={(ev) => (ev.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                >
                  {/* Avatar */}
                  <div style={UI.avatarCircle}>{initials}</div>

                  {/* Name + meta */}
                  <div style={UI.empInfo}>
                    <div style={UI.empName}>{e.name || "—"}</div>
                    <div style={UI.empMeta}>
                      {e.email && <span>{e.email}</span>}
                      {phone && e.email && <span style={{ color: "#e2e8f0" }}>·</span>}
                      {phone && <span>{phone}</span>}
                    </div>
                  </div>

                  {/* Role chip */}
                  <span style={UI.roleChip(role)}>{role}</span>

                  {/* Status badge — clicking stops row navigation */}
                  <div onClick={(ev) => ev.stopPropagation()}>
                    <select
                      value={status}
                      onChange={(ev) => onUpdateStatus(e.id, ev.target.value)}
                      style={{
                        ...UI.statusBadge(statusKey),
                        border: `1px solid`,
                        cursor: "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                        paddingRight: 24,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 8px center",
                        outline: "none",
                      }}
                    >
                      {EMPLOYEE_STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
